import { useEffect, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { cmsApi } from '../lib/api/cms';

const Hero = () => {
  const { t } = useTranslation();
  const [heroImage, setHeroImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Default fallback image
  const defaultImage = "https://images.pexels.com/photos/167698/pexels-photo-167698.jpeg?auto=compress&cs=tinysrgb&w=1920";

  useEffect(() => {
    loadHeroImage();
  }, []);

  const loadHeroImage = async () => {
    try {
      console.log('Loading hero image from CMS...');
      setError(null);
      
      const { data: heroSection, error } = await cmsApi.getSection('hero');
      if (error) {
        console.error('Error loading hero section:', error);
        setError('Failed to load hero section');
        setHeroImage(defaultImage);
        return;
      }

      console.log('Hero section data:', heroSection);

      // Use uploaded image if available, otherwise use default
      const imageUrl = heroSection?.background_image || defaultImage;
      console.log('Setting hero image to:', imageUrl);
      setHeroImage(imageUrl);
      
    } catch (err) {
      console.error('Exception loading hero image:', err);
      setError('Exception occurred while loading hero image');
      setHeroImage(defaultImage);
    } finally {
      setLoading(false);
    }
  };

  // Listen for CMS updates
  useEffect(() => {
    const handleCMSUpdate = () => {
      console.log('CMS update detected, reloading hero image...');
      loadHeroImage();
    };

    window.addEventListener('translationsUpdated', handleCMSUpdate);
    return () => {
      window.removeEventListener('translationsUpdated', handleCMSUpdate);
    };
  }, []);

  useEffect(() => {
    // Smooth scroll for anchor links
    const handleAnchorClick = (e: Event) => {
      const target = e.target as HTMLAnchorElement;
      if (target.hash && target.hash !== '#') {
        e.preventDefault();
        const element = document.querySelector(target.hash);
        if (element) {
          window.scrollTo({
            top: element.getBoundingClientRect().top + window.scrollY - 80,
            behavior: 'smooth'
          });
        }
      }
    };

    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', handleAnchorClick);
    });

    return () => {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.removeEventListener('click', handleAnchorClick);
      });
    };
  }, []);

  return (
    <section id="hero" className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        {loading ? (
          <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
            <div className="text-gray-500">Loading hero image...</div>
          </div>
        ) : (
          <>
            <img 
              src={heroImage || defaultImage}
              alt="Forest landscape" 
              className="w-full h-full object-cover"
              fetchPriority="high"
              onLoad={() => console.log('Hero image loaded successfully:', heroImage)}
              onError={(e) => {
                console.error('Hero image failed to load:', heroImage);
                // Fallback to default image if CMS image fails to load
                const target = e.target as HTMLImageElement;
                if (target.src !== defaultImage) {
                  console.log('Falling back to default image');
                  target.src = defaultImage;
                  setHeroImage(defaultImage);
                }
              }}
            />
            {error && (
              <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded text-sm">
                {error}
              </div>
            )}
          </>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/70 to-primary-900/50"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="animate-float">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 drop-shadow-lg">
            {t('hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8 drop-shadow">
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#services" className="px-8 py-3 bg-primary-700 hover:bg-primary-800 text-white rounded-lg shadow-lg transition-all hover:scale-105 font-medium">
              {t('hero.servicesButton')}
            </a>
            <a href="#contact" className="px-8 py-3 bg-white/90 hover:bg-white text-primary-800 rounded-lg shadow-lg transition-all hover:scale-105 font-medium">
              {t('hero.contactButton')}
            </a>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 flex flex-col items-center">
        <span className="text-white/80 text-sm mb-2">{t('hero.scrollText')}</span>
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center p-1">
          <div className="w-1.5 h-3 bg-white/80 rounded-full animate-bounce"></div>
        </div>
      </div>
      
      {/* Decorative elements */}
      <div className="absolute -bottom-2 left-0 right-0 z-10">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
          <path fill="#f9fafb" fillOpacity="1" d="M0,64L80,69.3C160,75,320,85,480,90.7C640,96,800,96,960,85.3C1120,75,1280,53,1360,42.7L1440,32L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
        </svg>
      </div>
    </section>
  );
};

export default Hero;